import React, { useRef } from "react";

import ShipeeziNewGrid, { Column, GeneralActions } from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";

import Breadcrumb from "../../../../../../components/Generic/Breadcrump";
import { MoreOptions } from "../../../../../../components/MoreOptionsComponent/MoreOptions";
import SendEmailModal, { SendEmailHandles } from "../../../../../../components/SendEmail";
import { Cronjob } from "../../../../../../models/cronjob";
import { CronjobFrequency, Status, WeekDays } from "../../../../../../util/enums";
import {
  formatDateWithoutTimeZone,
  formatTimeWithoutTimeZone,
} from "../../../../../../util/Format";
import DeleteConfirmationModal, {
  DeleteConfirmationModalHandles,
} from "./components/DeleteConfirmationModal";
import useActions from "./hooks/useActions";
import { BadgeActive, BadgeInactive, Wrapper } from "./styles";

/**
 * This component is for Cronjob automation
 * Being used in SP+ and SP
 */
const AutomatePageComponent: React.FC = () => {
  const { t: translate } = useTranslation();

  const manageModalRef = useRef<SendEmailHandles>(null);

  const deleteConfirmationModalRef = useRef<DeleteConfirmationModalHandles>(null);

  const {
    variables,
    setCurrentPage,
    setPageSize,
    handleFilter,
    handleSearch,
    handleSort,
    useUpdateCronjob,
    useDeleteCronjob,
  } = useActions();

  const { mutate: updateCronjob, isLoading: updatingCronjob } = useUpdateCronjob();
  const { mutate: deleteCronjob, isLoading: deletingCronjob } = useDeleteCronjob();

  const daysOfTheWeek = [
    translate(WeekDays.SUNDAY),
    translate(WeekDays.MONDAY),
    translate(WeekDays.TUESDAY),
    translate(WeekDays.WEDNESDAY),
    translate(WeekDays.THURSDAY),
    translate(WeekDays.FRIDAY),
    translate(WeekDays.SATURDAY),
  ];

  const badgeStatus = (isRunning: boolean) => {
    if (isRunning) {
      return <BadgeActive>{Status.ACTIVE}</BadgeActive>;
    }

    return <BadgeInactive>{Status.INACTIVE}</BadgeInactive>;
  };

  const formatFrequency = (cronjob: Cronjob) => {
    switch (cronjob.frequency) {
      case CronjobFrequency.DAILY:
        return translate("daily");
      case CronjobFrequency.WEEKLY:
        if (cronjob?.day === undefined || cronjob.day === null) return;

        return `${translate("weekly")} ${translate("on").toLocaleLowerCase()} ${
          daysOfTheWeek[cronjob?.day] || ""
        }`;
      case CronjobFrequency.MONTHLY:
        return `${translate("monthly")} ${translate("on").toLocaleLowerCase()} ${
          cronjob?.day || ""
        }th`;
      default:
        return "";
    }
  };

  const columns: Column[] = [
    { name: "name", title: translate("name") },
    {
      name: "contactEmails",
      title: translate("sentToEmail"),
      searchIdentifier: "cronjobContacts.mail",
      formatComponent: (emails: string[]) => {
        return (
          <MoreOptions options={emails} title={translate("sentToEmail")} manyOptionsToShow={1} />
        );
      },
    },
    {
      name: "frequency",
      title: translate("frequency"),
      hideSearch: true,
      hideSort: true,
      formatComponent: (_, row) => formatFrequency(row),
    },
    {
      name: "time",
      title: translate("time"),
      hideSearch: true,
      formatText: (value: string) => formatTimeWithoutTimeZone(value),
    },
    {
      name: "createdAt",
      title: translate("createdAt"),
      hideSearch: true,
      hideSort: true,
      formatText: (value: string) => formatDateWithoutTimeZone(value, true),
    },
    {
      name: "isRunning",
      title: translate("status"),
      hideSearch: true,
      formatComponent: (value: boolean) => badgeStatus(value),
    },
  ];

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleFilterBy"],
      right: ["handleActive", "handleNewRecord"],
    },
    handleSearch: {
      onSearch: handleSearch,
    },
    handleFilterBy: {
      categories: [
        {
          name: translate("status"),
          column: "isRunning",
          options: [
            { label: Status.ACTIVE, value: "1" },
            { label: Status.INACTIVE, value: "0" },
          ],
        },
        {
          name: translate("frequency"),
          column: "frequency",
          options: [
            { label: translate("daily"), value: CronjobFrequency.DAILY },
            { label: translate("weekly"), value: CronjobFrequency.WEEKLY },
            { label: translate("monthly"), value: CronjobFrequency.MONTHLY },
          ],
        },
      ],
      onChange: handleFilter,
    },
  };

  const handleEditCronjob = (cronjob: Cronjob) => {
    if (!cronjob || !cronjob?.id) return;

    manageModalRef.current?.open(cronjob);
  };

  const handleChangeStatus = (cronjob: Cronjob) => {
    if (!cronjob || !cronjob.id) return;

    updateCronjob({ id: cronjob.id, cronjob: { ...cronjob, isRunning: !cronjob.isRunning } });
  };

  const handleDeleteCronjob = (cronjob: Cronjob) => {
    if (!cronjob || !cronjob.id) return;

    deleteConfirmationModalRef.current?.open(cronjob);
  };

  return (
    <Wrapper data-cy="automateDataList">
      {/* <header className="mb-4">
        <Breadcrumb
          items={[
            { title: translate("configurations") },
            {
              title: translate("automate"),
              color: "#465869",
              dataCy: "breadcrumbsSubTitle",
            },
          ]}
        />
      </header> */}

      <ShipeeziNewGrid
        data={variables.cronjobs}
        columns={columns}
        totalItems={variables.total}
        loadingData={variables.isFetching || updatingCronjob}
        pageSize={variables.pageSize}
        currentPage={variables.page}
        emptyData={translate("noInformationToBeShown")}
        pageSizeList={[20, 40, 60, 100]}
        onChangePageSize={setPageSize}
        onNextPage={setCurrentPage}
        onPreviousPage={setCurrentPage}
        onGoToPage={setCurrentPage}
        onSorting={handleSort}
        rowActions={{
          orderBy: ["handleEdit", "handleChange", "handleDelete"],
          handleEdit: (_, row) => handleEditCronjob(row),
          handleChange: (_, row) => handleChangeStatus(row),
          handleDelete: (_, row) => handleDeleteCronjob(row),
        }}
        positionActionsColumn="right"
        generalActions={generalActions}
      />

      <SendEmailModal
        title={translate("editAutomation")}
        ref={manageModalRef}
        onSubmit={(cronjob) => {
          if (!cronjob?.id || !cronjob?.uuid) return;
          updateCronjob(
            { id: cronjob.id, cronjob },
            { onSuccess: () => manageModalRef.current?.close() }
          );
        }}
        isLoading={updatingCronjob}
      />

      <DeleteConfirmationModal
        onSubmit={(id) =>
          deleteCronjob(id, { onSuccess: () => deleteConfirmationModalRef.current?.close() })
        }
        loadingAction={deletingCronjob}
        ref={deleteConfirmationModalRef}
      />
    </Wrapper>
  );
};

export default AutomatePageComponent;
